/**
 * @documental
 * @export Placeholder
 * @name
 * Button.Placeholder
 */
import { CSSProperties, FC, PropsWithChildren, useMemo } from "react";
import Skeleton from "react-loading-skeleton";

import { BaseProps } from "../../BaseProps";
import { Shelf } from "../../layout";
import { createComponentStyleHook } from "../../tokens";
import { Semibold } from "../../typography";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import classNames from "classnames";

import { ButtonAppearance } from "./ButtonAppearances";
import { ButtonSize } from "./ButtonSize";

import tokenStyles from "./styles.module.css";

/**
 * The `Placeholder` component properties.
 */
export interface PlaceholderProps extends BaseProps {
  /**
   * Indicates whether the placeholder should reserve space for the icon.
   */
  readonly icon?: boolean;

  /**
   * The button appearance.
   */
  readonly appearance: ButtonAppearance;

  /**
   * The button size. If not specified defaults to `"large"`.
   */
  readonly size?: ButtonSize;
}

/**
 * Builds inline styles for the placeholder span from a ButtonAppearance.
 */
function getPlaceholderStyle(appearance: ButtonAppearance, iconOnly: boolean): CSSProperties {
  const { vertical } = appearance.padding;
  let { horizontal } = appearance.padding;
  if (iconOnly) horizontal = vertical;

  return {
    display: "inline-block",
    letterSpacing: `${appearance.label.letterSpacing}px`,
    whiteSpace: "nowrap",
    padding: `calc(${vertical / 16}rem + 1px) calc(${horizontal / 16}rem + 1px)`,
    fontSize: `${appearance.label.fontSize / 16}rem`,
    lineHeight: `${appearance.label.lineHeight / 16}rem`,
    borderRadius: "0.25rem",
    overflow: "hidden",
  };
}

const useResolvedPlaceholderStyles = createComponentStyleHook<PlaceholderProps>({
  legacyStyles: () => import("./legacy/styles.module.scss"),
  tokenStyles,
  componentTokens: "button",
});

/**
 * A loading placeholder for the `Button` component.
 */
export const Placeholder: FC<PropsWithChildren<PlaceholderProps>> = (props) => {
  const { children, icon = false, appearance, size = "large" } = props;
  const resolvedSize = appearance.className?.match(/\b(large|small|compact)\b/)?.[1] ?? size;
  const { isLegacy, styles, mode, StyleModeBoundary } = useResolvedPlaceholderStyles(props);

  const iconOnly = icon && !children;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      tokenStyles.ButtonPlaceholder,
      styles.ButtonPlaceholder,
      "button-placeholder",
      resolvedSize,
      {
        "icon-only": iconOnly,
      },
    ],
    testIdPrefix: "button-placeholder",
  });

  const placeholderStyle = useMemo(
    () => (isLegacy ? getPlaceholderStyle(appearance, !!iconOnly) : undefined),
    [isLegacy, appearance, iconOnly],
  );

  const placeholderContent = (
    <>
      <Skeleton containerClassName={classNames(tokenStyles.ButtonSkeleton, styles.ButtonSkeleton)} />
      <Shelf spacing="small">
        {icon && <span className={classNames(tokenStyles.IconPlaceholder, styles.IconPlaceholder, "icon-placeholder")} />}
        {!iconOnly && <Semibold>{children ?? <>&nbsp;</>}</Semibold>}
      </Shelf>
    </>
  );

  return (
    <StyleModeBoundary mode={mode}>
      <span {...baseHtmlProps} style={{ ...baseHtmlProps.style, ...placeholderStyle }} role="button" aria-busy>
        {placeholderContent}
      </span>
    </StyleModeBoundary>
  );
};
